import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import useWindowWidth from '../../hooks/useWindowWidth';
import './Dashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const fmt = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
const HIDDEN = '••••••';

function StatusDonut({ title, paid, pending, overdue, paidLabel, hideValues }) {
    const total = paid.total + pending.total + overdue.total;
    const paidPct = total > 0 ? Math.round((paid.total / total) * 100) : 0;

    const data = {
        labels: [paidLabel, 'Pendente', 'Atrasada'],
        datasets: [{
            data: [paid.total, pending.total, overdue.total],
            backgroundColor: ['rgba(16,185,129,0.8)', 'rgba(245,158,11,0.8)', 'rgba(239,68,68,0.8)'],
            borderColor:     ['rgba(16,185,129,1)',   'rgba(245,158,11,1)',   'rgba(239,68,68,1)'],
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8, font: { size: 11 } } },
            title: { display: false },
            tooltip: {
                callbacks: {
                    label: ctx => ` ${ctx.label}: ${fmt(ctx.raw)}`,
                },
            },
        },
    };

    return (
        <div className="psc-half">
            <p className="psc-title">{title}</p>
            <div className="psc-donut-wrap">
                {total > 0
                    ? <Doughnut data={data} options={options} />
                    : <div className="psc-empty">Sem dados</div>}
                {total > 0 && (
                    <div className="psc-center-label">
                        <span className="psc-pct">{paidPct}%</span>
                        <span className="psc-pct-label">quitado</span>
                    </div>
                )}
            </div>
            <div className="psc-legend">
                <div className="psc-legend-row psc-paid">
                    <span>{paidLabel}</span><span>{hideValues ? HIDDEN : fmt(paid.total)}</span>
                </div>
                <div className="psc-legend-row psc-pending">
                    <span>Pendente</span><span>{hideValues ? HIDDEN : fmt(pending.total)}</span>
                </div>
                <div className="psc-legend-row psc-overdue">
                    <span>Atrasada</span><span>{hideValues ? HIDDEN : fmt(overdue.total)}</span>
                </div>
            </div>
        </div>
    );
}

function PaymentStatusChart({ data, hideValues }) {
    const width = useWindowWidth();
    const isMobile = width < 768;

    if (!data) return null;

    const rev = data.revenue;
    const exp = data.expense;

    const inner = (
        <div className="psc-container">
            <StatusDonut
                title="Saúde das Receitas"
                paid={{ total: rev.recebida.total }}
                pending={{ total: rev.pendente.total }}
                overdue={{ total: rev.atrasada.total }}
                paidLabel="Recebida"
                hideValues={hideValues}
            />
            <div className="psc-divider" />
            <StatusDonut
                title="Saúde das Despesas"
                paid={{ total: exp.paga.total }}
                pending={{ total: exp.pendente.total }}
                overdue={{ total: exp.atrasada.total }}
                paidLabel="Paga"
                hideValues={hideValues}
            />
        </div>
    );

    if (isMobile) {
        return (
            <div className="dash-chart-section-m">
                <p className="dash-chart-title-m">Saúde Financeira do Período</p>
                {inner}
            </div>
        );
    }

    return (
        <div className="card shadow-sm">
            <div className="card-header py-2">
                <strong>Saúde Financeira do Período</strong>
            </div>
            <div className="card-body">
                {inner}
            </div>
        </div>
    );
}

export default PaymentStatusChart;
